import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";
import useDebouncedSearch from "../hooks/useDebouncedSearch";
import styles from "../styles/adminBrandsComponent.module.css";
import btnStyles from "../styles/globalButtonStylng.module.css";
import { AdminInstance } from "../utils/adminClass";
import { FormatInstance } from "../utils/formatClass";
import { SearchInstance } from "../utils/searchClass";
import { UsStateInstance } from "../utils/usStateClass";

export const AdminAddCustomerFormComponent = ({
  closeForm,
  data,
  setInputs,
  editing = false,
  setReset,
}: {
  closeForm: Function;
  data: any;
  setInputs: any;
  editing: Boolean;
  setReset: Function;
}) => {
  //prefilled with some defaults

  const [customerDetails, setCustomerDetails] = useState<Record<string, any>>({
    status: "pending",
    country: "USA",
  });
  const [canSubmit, setCanSubmit] = useState(false);
  const requiredFields = [
    //"venueId",
    //"brandId",
    "status",
    "contactName",
    "contactEmail",
    "contactPhone",
    //"paymentCustomerId", not sure how we should define this
    "paymentMethodType",
    "street",
    "city",
    "zip",
    "state",
    "country",
    "startDate",
    "endDate",
    "autoRenew",
  ];
  const { searchInput, debouncedSearchTerm, setSearchInput } = useDebouncedSearch();
  const [customer, setCustomer] = useState<any>(); //will be either brand or venue and we will check
  const [hideBorder, setHideBorder] = useState(true);
  const { searchType, search, searchResults } = useSelector((state: any) => state.search);
  const { states } = useSelector((state: any) => state.state);

  const deepEqual = (obj1: any, obj2: any) => {
    if (obj1 === obj2) return true;

    if (obj1 == null || obj2 == null) return false;

    if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
      if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  };

  //set the search type to only venues or brands that don't have customer associated
  useEffect(() => {
    SearchInstance.setSearchType("add-customer", setSearchInput);
    UsStateInstance.getUsStates();
    if (editing) {
      setCustomerDetails(data.customer);
    }
  }, []);

  useEffect(() => {
    //this should check to see if business has the key named "dispensaryType"
    if (customer === undefined) {
      console.log("business not set");
    } else {
      if (customer.hasOwnProperty("dispensaryType")) {
        setCustomerDetails({
          ...customerDetails,
          venueId: customer.id,
          brandId: null,
        });
      } else {
        setCustomerDetails({
          ...customerDetails,
          brandId: customer.id,
          venueId: null,
        });
      }
    }
  }, [customer]);

  //checks inputs to make sure all fields are filled
  useEffect(() => {
    const isFilled = requiredFields.every((field) => customerDetails[field]);

    if (editing) {
      setCanSubmit(isFilled && !deepEqual(data.customer, customerDetails));
    } else {
      setCanSubmit(isFilled);
    }

    console.log("cansubmit is", canSubmit, customerDetails);
  }, [customerDetails, data.customer, editing]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      SearchInstance.setSearchQuery(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  const submitCustomer = async (customer: any) => {
    console.log("submitting customer:", customer);

    const res = await AdminInstance.adminCreateCustomer(customer);

    if (res) {
      console.log("customer created:", res);
      closeForm();
    }
  };

  const updateCustomer = async (customer: any) => {
    console.log("updating customer with data:", customer);

    const res = await AdminInstance.updateCustomer(customer);

    if (res) {
      console.log("customer updated:", res);
      closeForm();
    }
  };

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    console.log(`form updating ${name} with ${value}`);
    setCustomerDetails({
      ...customerDetails,
      [name]: value,
    });
  };

  const handleSearchQueryChange = (event: any) => {
    setSearchInput(event.target.value);
  };

  return (
    <Form className={styles.wrapper} id="addCustomerForm">
      <Form.Group className="mb-3" controlId="formCustomerSearch">
        <Form.Label>Customer Name</Form.Label>
        <Form.Control
          type="text"
          name="searchInput"
          placeholder="Search for Brand or Venue"
          value={editing ? data.name : searchInput}
          onChange={handleSearchQueryChange}
        />
        <div className={`${styles.dropdown} ${hideBorder ? styles.hideBorder : ""}`}>
          {searchResults.map(
            (result: any, index: any) =>
              result.name !== searchInput && (
                <option
                  className={styles["option"]}
                  key={index}
                  value={editing ? customerDetails.id : result.id}
                  title={result.name}
                  onClick={() => {
                    console.log("here's selected", result);
                    setCustomer(result);
                    setSearchInput(result.name);
                    setHideBorder(true);
                  }}
                >
                  {result.name}
                </option>
              ),
          )}
        </div>
      </Form.Group>
      <Form.Group className="mb-3" controlId="formFirstName">
        <Form.Label>Primary Contact</Form.Label>
        <Form.Control type="text" name="contactName" onChange={handleInputChange} value={customerDetails.contactName} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formEmail">
        <Form.Label>Email Address</Form.Label>
        <Form.Control
          type="email"
          placeholder="Enter email"
          name="contactEmail"
          value={customerDetails.contactEmail}
          onChange={handleInputChange}
        />
        <Form.Text className="text-muted">We never share emails with anyone else.</Form.Text>
      </Form.Group>
      <Form.Group className="mb-3" controlId="formPhone">
        <Form.Label>Phone Number</Form.Label>
        <Form.Control
          type="email"
          placeholder="Enter Phone Number"
          name="contactPhone"
          value={customerDetails.contactPhone}
          onChange={handleInputChange}
        />
        <Form.Text className="text-muted">Please include your area code</Form.Text>
      </Form.Group>
      <Form.Group className="mb-3" controlId="formStreet">
        <Form.Label>Street Address</Form.Label>
        <Form.Control
          type="text"
          placeholder="Street Address"
          name="street"
          value={customerDetails.street}
          onChange={handleInputChange}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formZip">
        <Form.Label>Zip</Form.Label>
        <Form.Control
          type="text"
          placeholder="Zip Code"
          name="zip"
          value={customerDetails.zip}
          onChange={handleInputChange}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formCity">
        <Form.Label>City</Form.Label>
        <Form.Control
          type="text"
          placeholder="City"
          name="city"
          value={customerDetails.city}
          onChange={handleInputChange}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formBasicState">
        <Form.Label>State</Form.Label>
        <div>
          <select
            //value={customerDetails.stateId}
            value={customerDetails.state}
            name=""
            id=""
            onChange={(e) =>
              setCustomerDetails({
                ...customerDetails,
                //stateId: Math.floor(Number(e.target.value)),
                state: e.target.value,
              })
            }
          >
            {states.map((state: any) => (
              <option key={state.id} value={state.abbr}>
                {state.abbr}
              </option>
            ))}
          </select>
        </div>
      </Form.Group>
      <Form.Group className="mb-3" controlId="formStartDate">
        <Form.Label>Start Date</Form.Label>
        <Form.Control
          type="date"
          placeholder="Start Date"
          name="startDate"
          value={FormatInstance.formatCalendarDate(customerDetails.startDate)}
          onChange={handleInputChange}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formEndDate">
        <Form.Label>End Date</Form.Label>
        <Form.Control
          type="date"
          placeholder="End Date"
          name="endDate"
          value={FormatInstance.formatCalendarDate(customerDetails.endDate)}
          onChange={handleInputChange}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="autoRenew">
        <Form.Label>Autorenew</Form.Label>
        <div>
          <Form.Check
            inline
            checked={customerDetails.autoRenew === true}
            type="radio"
            label="Yes"
            name="autoRenew"
            value="true"
            onChange={handleInputChange}
          />
          <Form.Check
            inline
            checked={customerDetails.autoRenew === false}
            type="radio"
            label="No"
            name="autoRenew"
            value="false"
            onChange={handleInputChange}
          />
        </div>
      </Form.Group>
      <Form.Group className="mb-3" controlId="formBasicStatus">
        <Form.Label>Status</Form.Label>
        <div>
          <select
            value={customerDetails.status}
            name=""
            id=""
            onChange={(e) =>
              setCustomerDetails({
                ...customerDetails,
                //stateId: Math.floor(Number(e.target.value)),
                status: e.target.value,
              })
            }
          >
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </Form.Group>
      <Form.Group className="mb-3" controlId="paymentMethodType">
        <Form.Label>Payment Type</Form.Label>
        <Form.Control
          type="text"
          placeholder="stripe, paypal, zelle, etc"
          name="paymentMethodType"
          onChange={handleInputChange}
          value={customerDetails.paymentMethodType}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="notes">
        <Form.Label>Notes</Form.Label>
        <Form.Control
          type="text"
          placeholder="Notes on customer"
          name="notes"
          value={customerDetails.notes}
          onChange={handleInputChange}
        />
      </Form.Group>
      <Button
        className={btnStyles.Submit}
        disabled={!canSubmit}
        variant="primary"
        type="submit"
        onClick={(e) => {
          e.preventDefault();
          editing ? updateCustomer(customerDetails) : submitCustomer(customerDetails);
          // closeForm();
        }}
      >
        {editing ? "Update Customer" : "Create Customer"}
      </Button>
    </Form>
  );
};
